import { useCallback, useMemo, useState } from "react";
import { EditQuizFilterType } from "../../components/EditQuizFilter";

type UseListControlsProps<T, C extends string> = {
	allItems: T[];
	onUpdate: (newItems: Set<string>) => void;
} & Pick<UseGroupedListProps<T, C>, "keyExtractor" | "categoryExtractor" | "initialSelected" | "isInitialSelected">;

function useListControls<T, C extends string>({
	initialSelected,
	allItems,
	onUpdate,
	categoryExtractor,
	keyExtractor,
	isInitialSelected,
}: UseListControlsProps<T, C>) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
		const processed = allItems.filter((item) => isInitialSelected(item, initialSelected));
		const ids = processed.map((item) => keyExtractor(item));
		return new Set(ids);
	});

	const [expandedCategories, setExpandedCategories] = useState<Set<C>>(() => {
		const categoriesWithSelection = new Set<C>();
		const allCategories = new Set<C>();

		allItems.forEach((item) => {
			const cat = categoryExtractor(item);
			allCategories.add(cat);

			if (isInitialSelected(item, initialSelected)) {
				categoriesWithSelection.add(cat);
			}
		});

		// Logic: If none are selected -> expand all.
		// If some are selected -> only expand those with selections.
		return categoriesWithSelection.size > 0 ? categoriesWithSelection : allCategories;
	});
	const handleSelectAllInCategory = (items: T[], isSelecting: boolean) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			items.forEach((item) => {
				if (isSelecting) {
					next.add(keyExtractor(item));
				} else {
					next.delete(keyExtractor(item));
				}
			});
			onUpdate(next);
			return next;
		});
	};

	const handleToggleItem = (item: T, isSelected: boolean) => {
		const id = keyExtractor(item);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (isSelected) {
				next.add(id);
			} else {
				next.delete(id);
			}
			onUpdate(next);
			return next;
		});
	};

	const handleCategoryExpand = (category: C, open?: boolean) => {
		setExpandedCategories((prev) => {
			const next = new Set(prev);

			if (open !== undefined) {
				if (open) {
					next.add(category);
				} else {
					next.delete(category);
				}
				return next;
			}

			if (next.has(category)) {
				next.delete(category);
			} else {
				next.add(category);
			}
			return next;
		});
	};

	return {
		handleCategoryExpand,
		handleToggleItem,
		handleSelectAllInCategory,
		expandedCategories,
		selectedIds,
		setSelectedIds,
	};
}

type Category<T, C extends string> = {
	category: C;
	items: T[];
	count: number;
};

type UseListControlsReturn = ReturnType<typeof useListControls>;

type UseFilteredItemsProps<T, C extends string> = {
	masterList: T[];
	activeStatus: EditQuizFilterType;
	searchFilter: string;
} & Pick<UseListControlsReturn, "selectedIds"> &
	Pick<UseGroupedListProps<T, C>, "keyExtractor" | "searchSelector" | "categoryExtractor" | "sortFunction">;

function useFilteredItems<T, C extends string>({
	masterList,
	searchFilter,
	activeStatus,
	selectedIds,
	sortFunction,
	searchSelector,
	keyExtractor,
	categoryExtractor,
}: UseFilteredItemsProps<T, C>) {
	const displayedItems = useMemo(() => {
		const unsortedList = masterList.filter((item) => {
			const searchText = searchSelector(item);
			const matchesSearch = !searchFilter || searchText.toLowerCase().includes(searchFilter);
			const isSelected = selectedIds.has(keyExtractor(item));
			const matchesStatus =
				(isSelected && activeStatus !== "notSelected") || (!isSelected && activeStatus !== "selected");

			return matchesSearch && matchesStatus;
		});

		if (sortFunction) {
			return unsortedList.sort(sortFunction);
		}
		return unsortedList;
	}, [masterList, sortFunction, searchSelector, searchFilter, selectedIds, keyExtractor, activeStatus]);

	const groupedItems = useMemo(() => {
		// 1. Get unique categories present in the current data
		const uniqueCategories = Array.from(new Set(displayedItems.map((item) => categoryExtractor(item))));

		// 2. Build the groups based on those unique values
		return uniqueCategories.map((category) => {
			const items = displayedItems.filter((item) => categoryExtractor(item) === category);
			return {
				category,
				items,
				count: items.length,
			} as Category<T, C>;
		});
	}, [categoryExtractor, displayedItems]);

	return {
		displayedItems,
		groupedItems,
	};
}

type UseGroupedListProps<T, C extends string> = {
	/** string[] from parent */
	initialSelected: string[];
	initialItems: T[];
	/**Extracts id or key used for selected ids */
	keyExtractor: (item: T) => string;
	/** How to get the string for searching */
	searchSelector: (item: T) => string;
	categoryExtractor: (item: T) => C;
	/** Sends back processed items using formatForParent */
	onUpdate: (items: string[]) => void;
	formatForParent: (item: T) => string | string[];
	sortFunction?: (a: T, b: T) => number;
	//  How to check if an item is considered "selected" based on parent strings
	isInitialSelected: (item: T, rawStrings: string[]) => boolean;
};

export default function useGroupedList<T, C extends string>({
	initialItems,
	initialSelected,
	keyExtractor,
	onUpdate,
	categoryExtractor,
	searchSelector,
	sortFunction,
	formatForParent,
	isInitialSelected,
}: UseGroupedListProps<T, C>) {
	// Stores added items here
	const [customItems, setCustomItems] = useState<T[]>([]);
	const [activeStatus, setActiveStatus] = useState<EditQuizFilterType>("all");
	const [searchText, setSearchText] = useState("");
	const searchFilter = searchText.toLowerCase();

	const allItems = useMemo(() => [...initialItems, ...customItems], [customItems, initialItems]);

	const syncWithParent = useCallback(
		(nextSelectedIds: Set<string>) => {
			const formatted = allItems
				.filter((item) => nextSelectedIds.has(keyExtractor(item)))
				.flatMap((item) => {
					const result = formatForParent(item);
					return Array.isArray(result) ? result : [result];
				});

			onUpdate(Array.from(new Set(formatted))); // Remove duplicates
		},
		[allItems, formatForParent, keyExtractor, onUpdate],
	);

	const {
		expandedCategories,
		handleSelectAllInCategory,
		handleToggleItem,
		selectedIds,
		setSelectedIds,
		handleCategoryExpand,
	} = useListControls({
		allItems,
		categoryExtractor,
		initialSelected,
		keyExtractor,
		onUpdate: syncWithParent,
		isInitialSelected,
	});

	const { displayedItems, groupedItems } = useFilteredItems({
		activeStatus,
		categoryExtractor,
		keyExtractor,
		masterList: allItems,
		searchFilter,
		searchSelector,
		selectedIds,
		sortFunction,
	});

	const allSelected = useMemo(
		() => allItems.length > 0 && allItems.every((item) => selectedIds.has(keyExtractor(item))),
		[allItems, keyExtractor, selectedIds],
	);

	const handleSelectAll = useCallback(() => {
		const next = allSelected ? new Set<string>() : new Set(allItems.map((item) => keyExtractor(item)));
		setSelectedIds(next);
		syncWithParent(next);
	}, [allItems, allSelected, keyExtractor, setSelectedIds, syncWithParent]);

	const handleAddNewItem = useCallback(
		(newItem: T) => {
			setCustomItems((prev) => [...prev, newItem]);
			setSelectedIds((prevIds) => {
				const nextIds = new Set(prevIds).add(keyExtractor(newItem));
				const currentMaster = [...allItems, newItem];
				const selectedNamesWithIds = currentMaster
					.filter((item) => nextIds.has(keyExtractor(item)))
					.flatMap((item) => {
						const result = formatForParent(item);
						return Array.isArray(result) ? result : [result];
					});

				onUpdate(Array.from(new Set(selectedNamesWithIds)));
				return nextIds;
			});
		},
		[allItems, formatForParent, keyExtractor, onUpdate, setSelectedIds],
	);

	const isCategoryExapnded = useCallback(
		(category: C) => {
			return expandedCategories.has(category);
		},
		[expandedCategories],
	);

	const isAllItemsInCategorySelected = useCallback(
		(category: Category<T, C>) => {
			return category.items.every((item) => selectedIds.has(keyExtractor(item)));
		},
		[keyExtractor, selectedIds],
	);

	const isItemSelected = useCallback(
		(item: T) => {
			return selectedIds.has(keyExtractor(item));
		},
		[keyExtractor, selectedIds],
	);

	const isCategoryIndeterminate = useCallback(
		(category: Category<T, C>) => {
			const itemsInCategory = category.items;
			// 1. Count how many items in this category are in the selectedIds set
			const selectedCount = itemsInCategory.filter((item) => selectedIds.has(keyExtractor(item))).length;

			// 2. It's indeterminate if some are selected, but not all
			return selectedCount > 0 && selectedCount < itemsInCategory.length;
		},
		[keyExtractor, selectedIds],
	);

	const getNumItemsSelectedInCategory = useCallback(
		(category: Category<T, C>) => category.items.filter((item) => selectedIds.has(keyExtractor(item))).length,
		[keyExtractor, selectedIds],
	);

	return {
		filter: {
			searchText,
			setSearchText,
			activeStatus,
			setActiveStatus,
		},
		items: {
			groupedItems,
			displayedItems,
		},
		actions: {
			handleAddNewItem,
			handleSelectAll,
			handleCategoryExpand,
			handleSelectAllInCategory,
			handleToggleItem,
		},
		state: {
			isCategoryExapnded,
			isAllItemsInCategorySelected,
			isCategoryIndeterminate,
			getNumItemsSelectedInCategory,
			isItemSelected,
			allSelected,
			numSelected: selectedIds.size,
		},
	};
}
